import { clsx } from 'clsx'
import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ApiError } from '@/lib/http'
import { Button } from './primitives'

/** Inline failure block: says what broke, and offers the one useful action. */
export function ErrorState({
  error,
  onRetry,
  compact,
  className,
}: {
  error: unknown
  onRetry?: () => void
  compact?: boolean
  className?: string
}) {
  const offline = error instanceof ApiError && error.status === 0
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : 'エラーが発生しました'
  const status = error instanceof ApiError && error.status > 0 ? error.status : null
  // The backend ships a "lite" build that disables whole feature areas.
  // Saying so is more useful than showing a bare 403.
  const lite = status === 403 && /UI_MODE\s*=\s*lite/i.test(raw)
  const message = lite ? 'この機能はサーバー側で無効になっています（UI_MODE=lite）' : raw

  return (
    <div
      role="alert"
      className={clsx(
        'flex items-start gap-3 rounded-lg border border-negative/25 bg-negative/5',
        compact ? 'p-3' : 'p-4',
        className,
      )}
    >
      <span className="mt-0.5 shrink-0 text-negative">
        {offline ? <WifiOff className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-negative">{message}</p>
        {lite ? (
          <p className="mt-0.5 text-2xs leading-relaxed text-ink-3">
            画面は使えますが、データはフル構成のバックエンドでのみ返ります
          </p>
        ) : status ? (
          <p className="mt-0.5 text-2xs text-ink-3">HTTP {status}</p>
        ) : null}
      </div>
      {onRetry && !lite ? (
        <Button size="sm" variant="secondary" icon={<RefreshCw className="h-3.5 w-3.5" />} onClick={onRetry}>
          再試行
        </Button>
      ) : null}
    </div>
  )
}

interface BoundaryState {
  error: Error | null
}

/** Keeps one broken screen from taking down the whole shell. */
export class RouteErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route crashed:', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="mx-auto max-w-lg py-16">
          <ErrorState
            error={this.state.error}
            onRetry={() => {
              this.setState({ error: null })
              window.location.reload()
            }}
          />
        </div>
      )
    }
    return this.props.children
  }
}
